import React from 'react';
import { Caption, CaptionsSettings } from '../../types/editor';
import { Type, Sparkles, Plus, Trash2, Clock } from 'lucide-react';

interface CaptionsPanelProps {
  captions: Caption[];
  settings: CaptionsSettings;
  isGenerating: boolean;
  onUpdateCaption: (id: string, updates: Partial<Caption>) => void;
  onDeleteCaption: (id: string) => void;
  onAddCaption: () => void;
  onSettingsChange: (updates: Partial<CaptionsSettings>) => void;
  onAutoGenerate: () => void;
}

export default function CaptionsPanel({
  captions,
  settings,
  isGenerating,
  onUpdateCaption,
  onDeleteCaption,
  onAddCaption,
  onSettingsChange,
  onAutoGenerate,
}: CaptionsPanelProps) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Auto Generate Button */}
      <div style={{ 
        padding: 12, 
        background: 'rgba(99,102,241,0.1)', 
        borderRadius: 8,
        border: '1px solid rgba(99,102,241,0.2)' 
      }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '0.8rem', color: '#fff', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Sparkles size={14} color="#818cf8" /> Legendas Inteligentes
        </h3>
        <p style={{ margin: '0 0 12px', fontSize: '0.65rem', color: 'rgba(255,255,255,0.6)' }}>
          Gere legendas sincronizadas automaticamente a partir do áudio do vídeo, com sugestões de destaque usando IA.
        </p>
        <button
          onClick={onAutoGenerate}
          disabled={isGenerating}
          style={{
            width: '100%',
            padding: '8px',
            background: isGenerating ? 'rgba(255,255,255,0.1)' : '#4f46e5',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: '0.75rem',
            fontWeight: 600,
            cursor: isGenerating ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6
          }}
        >
          {isGenerating ? 'Analisando Áudio...' : 'Gerar Legendas (1 Crédito)'}
        </button>
      </div>

      {/* Global Settings */}
      <div>
        <h4 style={{ margin: '0 0 8px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)' }}>Estilo Visual</h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Cor do Texto</label>
            <input 
              type="color" 
              value={settings.textColor} 
              onChange={e => onSettingsChange({ textColor: e.target.value })}
              style={{ width: '100%', height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Cor do Destaque</label>
            <input 
              type="color" 
              value={settings.impactTextColor} 
              onChange={e => onSettingsChange({ impactTextColor: e.target.value })}
              style={{ width: '100%', height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Fundo</label>
            <input 
              type="color" 
              value={settings.bgColor} 
              onChange={e => onSettingsChange({ bgColor: e.target.value })}
              style={{ width: '100%', height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Opacidade do Fundo</label>
            <input 
              type="range" 
              min="0" max="1" step="0.1" 
              value={settings.bgOpacity} 
              onChange={e => onSettingsChange({ bgOpacity: parseFloat(e.target.value) })}
              style={{ width: '100%', accentColor: '#4f46e5' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Fonte</label>
            <select
              value={settings.fontFamily}
              onChange={e => onSettingsChange({ fontFamily: e.target.value })}
              style={{ width: '100%', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 4, padding: '4px', fontSize: '0.65rem' }}
            >
              <option value="Inter">Inter</option>
              <option value="Roboto">Roboto</option>
              <option value="Montserrat">Montserrat</option>
              <option value="Oswald">Oswald</option>
              <option value="Playfair Display">Playfair</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Posição</label>
            <select
              value={settings.position}
              onChange={e => onSettingsChange({ position: e.target.value as CaptionsSettings['position'] })}
              style={{ width: '100%', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 4, padding: '4px', fontSize: '0.65rem' }}
            >
              <option value="top">Topo</option>
              <option value="center">Centro</option>
              <option value="bottom">Base</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.65rem', color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>Tamanho ({settings.fontSize}px)</label>
            <input 
              type="range" 
              min="20" max="120" step="1" 
              value={settings.fontSize} 
              onChange={e => onSettingsChange({ fontSize: parseInt(e.target.value) })}
              style={{ width: '100%', accentColor: '#4f46e5' }}
            />
          </div>
        </div>
      </div>

      {/* Captions List */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
          <h4 style={{ margin: 0, fontSize: '0.75rem', color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Type size={14} /> Trilhas de Texto
          </h4>
          <button 
            onClick={onAddCaption}
            style={{ background: 'transparent', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', borderRadius: 4, padding: '4px 8px', fontSize: '0.65rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <Plus size={12} /> Adicionar
          </button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {captions.length === 0 && (
            <div style={{ padding: 20, textAlign: 'center', fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>
              Nenhuma legenda adicionada
            </div>
          )}
          {captions.map((cap, index) => (
            <div key={cap.id} style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 8, padding: 8, border: '1px solid rgba(255,255,255,0.05)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
                <div style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', fontSize: '0.6rem', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>
                  #{index + 1}
                </div>
                <textarea 
                  value={cap.text}
                  onChange={e => onUpdateCaption(cap.id, { text: e.target.value })}
                  style={{ flex: 1, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 4, padding: '4px 8px', fontSize: '0.7rem', minHeight: 40, resize: 'vertical' }}
                  placeholder="Texto da legenda..."
                />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'rgba(0,0,0,0.2)', padding: '2px 6px', borderRadius: 4 }}>
                    <Clock size={12} color="rgba(255,255,255,0.4)" />
                    <input 
                      type="number" step="0.1" value={cap.startTime}
                      onChange={e => onUpdateCaption(cap.id, { startTime: parseFloat(e.target.value) || 0 })}
                      style={{ width: 40, background: 'transparent', border: 'none', color: '#fff', fontSize: '0.65rem' }}
                    />
                    <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.65rem' }}>-</span>
                    <input 
                      type="number" step="0.1" value={cap.endTime}
                      onChange={e => onUpdateCaption(cap.id, { endTime: parseFloat(e.target.value) || 0 })}
                      style={{ width: 40, background: 'transparent', border: 'none', color: '#fff', fontSize: '0.65rem' }}
                    />
                  </div>
                  
                  <select
                    value={cap.position || settings.position}
                    onChange={e => onUpdateCaption(cap.id, { position: e.target.value as CaptionsSettings['position'] })}
                    style={{ background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: '#fff', borderRadius: 4, padding: '2px 4px', fontSize: '0.58rem' }}
                  >
                    <option value="top">Topo</option>
                    <option value="center">Centro</option>
                    <option value="bottom">Base</option>
                  </select>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.65rem', color: cap.isImpactPhrase ? '#fbbf24' : 'rgba(255,255,255,0.5)', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={cap.isImpactPhrase}
                      onChange={e => onUpdateCaption(cap.id, { isImpactPhrase: e.target.checked })}
                      style={{ accentColor: '#fbbf24' }}
                    />
                    Impacto
                  </label>
                </div>
                <button 
                  onClick={() => onDeleteCaption(cap.id)}
                  style={{ background: 'transparent', border: 'none', color: '#ef4444', cursor: 'pointer', padding: 4 }}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
